import React, { useEffect } from 'react';
import './table-scroll.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/layout/Layout';
import SuperadminLayout from './components/layout/SuperadminLayout';
import Login from './pages/Login';
import PlayerSignup from './pages/PlayerSignup';
import ResetPassword from './pages/ResetPassword';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import Machines from './pages/Machines';
import Clients from './pages/Clients';
import Games from './pages/Games';
import Products from './pages/Products';
import Orders from './pages/Orders';
import Packages from './pages/Packages';
import Cashier from './pages/Cashier';
import Finance from './pages/Finance';
import Settings from './pages/Settings';
import PcClient from './pages/PcClient';
import Reports from './pages/Reports';
import Bookings from './pages/BookingsCalendar';
import Memberships from './pages/Memberships';
import Clans from './pages/Clans';
import Tournaments from './pages/Tournaments';
import PublicTournament from './pages/PublicTournament';
import ClientPortal from './pages/ClientPortal';
import ClientBooking from './pages/ClientBooking';
import SaEmpresas from './pages/superadmin/SaEmpresas';
import SaPlanos from './pages/superadmin/SaPlanos';
import SaIntegracoes from './pages/superadmin/SaIntegracoes';
import CRM from './pages/CRM';

// Área do operador (tenant). Superadmin é redirecionado para /superadmin.
function TenantRoute({ children }) {
  const { operator, loading } = useAuth();
  if (loading) return <div style={{ color: '#fff', padding: 40 }}>Carregando...</div>;
  if (!operator) return <Navigate to="/login" />;
  if (operator.role === 'superadmin') return <Navigate to="/superadmin" />;
  return children;
}

// Raiz pública: visitante vê a landing de divulgação; operador logado vai pro painel.
function Home() {
  const { operator, loading } = useAuth();
  if (loading) return <div style={{ color: '#fff', padding: 40 }}>Carregando...</div>;
  if (!operator) return <LandingPage />;
  if (operator.role === 'superadmin') return <Navigate to="/superadmin" replace />;
  return <Layout><Dashboard /></Layout>;
}

// Área da plataforma (só superadmin).
function SuperadminRoute({ children }) {
  const { operator, loading } = useAuth();
  if (loading) return <div style={{ color: '#fff', padding: 40 }}>Carregando...</div>;
  if (!operator) return <Navigate to="/login" />;
  if (operator.role !== 'superadmin') return <Navigate to="/" />;
  return children;
}

function App() {
  useEffect(() => {
    const isVisible = (element) => {
      const rect = element.getBoundingClientRect();
      const style = window.getComputedStyle(element);
      return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
    };
    const hasOpenOverlay = () => Array.from(document.body.querySelectorAll('div')).some((element) => {
      const rect = element.getBoundingClientRect();
      const style = window.getComputedStyle(element);
      const zIndex = Number.parseInt(style.zIndex || '0', 10) || 0;
      return style.position === 'fixed' && zIndex >= 40 && rect.width >= window.innerWidth * 0.7 && rect.height >= window.innerHeight * 0.7 && isVisible(element);
    });
    const onKeyDown = (event) => {
      if (event.key !== 'Escape' || !hasOpenOverlay()) return;
      const candidates = Array.from(document.querySelectorAll('button')).filter((button) => {
        const text = (button.textContent || '').trim().toLocaleLowerCase('pt-BR');
        return isVisible(button) && (text === '×' || text === 'x' || text === 'cancelar' || text === 'fechar');
      });
      const target = candidates[candidates.length - 1];
      if (!target) return;
      event.preventDefault();
      event.stopPropagation();
      target.click();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/criar-conta" element={<PlayerSignup />} />
          <Route path="/redefinir-senha" element={<ResetPassword />} />

          {/* Rotas Públicas de Campeonatos da Empresa */}
          <Route path="/:companySlug/campeonatos" element={<PublicTournament />} />
          <Route path="/:companySlug/campeonatos/:slug" element={<PublicTournament />} />
          <Route path="/:companySlug/campeonato/:slug" element={<PublicTournament />} />
          <Route path="/cliente/:slug" element={<ClientPortal />} />
          <Route path="/cliente/:companySlug/campeonatos" element={<PublicTournament />} />
          <Route path="/cliente/:companySlug/campeonatos/:slug" element={<PublicTournament />} />
          <Route path="/cliente/:slug/agendar/:machineId" element={<ClientBooking />} />
          <Route path="/campeonato/:slug" element={<PublicTournament />} />

          {/* RAIZ: landing pública (visitante) ou painel (operador logado) */}
          <Route path="/" element={<Home />} />

          {/* SUPERADMIN (plataforma) */}
          <Route path="/superadmin/*" element={
            <SuperadminRoute>
              <SuperadminLayout>
                <Routes>
                  <Route path="/"             element={<SaEmpresas />} />
                  <Route path="/plans"        element={<SaPlanos />} />
                  <Route path="/integrations" element={<SaIntegracoes />} />
                </Routes>
              </SuperadminLayout>
            </SuperadminRoute>
          } />

          {/* OPERADOR (tenant) */}
          <Route path="/*" element={
            <TenantRoute>
              <Layout>
                <Routes>
                  <Route path="/"         element={<Dashboard />} />
                  <Route path="/machines" element={<Machines />} />
                  <Route path="/clients"  element={<Clients />} />
                  <Route path="/games"    element={<Games />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/orders" element={<Orders />} />
                  <Route path="/packages" element={<Packages />} />
                  <Route path="/cashier"  element={<Cashier />} />
                  <Route path="/finance"  element={<Finance />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/reports"  element={<Reports />} />
                  <Route path="/bookings" element={<Navigate to="/bookings/calendar" replace />} />
                  <Route path="/bookings/calendar" element={<Bookings mode="calendar" />} />
                  <Route path="/bookings/manage" element={<Bookings mode="manage" />} />
                  <Route path="/memberships" element={<Memberships />} />
                  <Route path="/clans" element={<Clans />} />
                  <Route path="/tournaments" element={<Tournaments />} />
                  <Route path="/crm" element={<CRM initialMode="kanban" />} />
                  <Route path="/chatbot" element={<CRM initialMode="inbox" />} />
                </Routes>
              </Layout>
            </TenantRoute>
          } />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

function ComingSoon({ title }) {
  return (
    <div style={{ textAlign: 'center', padding: 60 }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🚧</div>
      <h2 style={{ color: '#fff' }}>{title}</h2>
      <p style={{ color: '#6b7280' }}>Em desenvolvimento — próxima versão</p>
    </div>
  );
}

export default App;
